import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { OAuthService } from 'src/app/shared/services/oauth.service';
import { ProjectService } from '../Projects/project.service';
import { EquipmentReservationService } from '../Projects/EquipmentReservations/equipment-reservation.service';
import { Subject, combineLatest, from, merge, of } from 'rxjs';
import { WarehouseDataService } from 'src/app/shared/services/warehouse-data.service';
import { Store } from '@ngrx/store';
import { selectCurrentProject, selectCurrentProjectId } from 'src/app/state/app.selectors';
import { catchError, distinctUntilChanged, take, takeUntil, tap } from 'rxjs/operators';
import { ArrivalService } from '../Projects/Arrivals/arrival.service';
import { BillingService } from '../billing/billing.service';
import { OnboardingService } from '../onboarding/onboarding.service';
import { ConfirmationService } from 'src/app/shared/services/utilities/confirmation.service';
import { OnboardingFormSummary } from 'src/app/shared/Models/onboarding-model';
import { Address } from 'src/app/shared/warehouse-models';
import { BaseComponent } from 'src/app/shared/components/base-component';
import { AnalyticsService } from 'src/app/shared/services/analytics.service';
import { LoggerService } from 'src/app/shared/services/logger.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { SystemAdminOnlyDirective } from 'src/app/shared/directives/system-admin-only.directive';
import { HomeCardComponent } from './home-card/home-card.component';
import { SafetyWidgetComponent } from '../Safety/safety-widget/safety-widget.component';
import { AnnouncementsWidgetComponent } from '../announcements/announcements-widget/announcements-widget.component';
import { AnnouncementNotificationService } from '../announcements/services/announcement-notification.service';

@Component({
    selector: 'app-home',
    templateUrl: 'home.component.html',
    styleUrls: ['./home.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SharedModule, SystemAdminOnlyDirective, HomeCardComponent, SafetyWidgetComponent, AnnouncementsWidgetComponent]
})
export class HomeComponent extends BaseComponent implements OnInit, OnDestroy {
  isLoading = true;
  readonly skeletonCards = Array(8).fill(0);
  currentProjectId: number = 0;
  arrivalCalendarEnabled = false;
  liftCalendarEnabled = false;
  private destroy$ = new Subject<void>();
  address?: Address;
  /** Feeds the "Omat tiedotteet" tile badge — the shell owns unread/attention tracking. */
  attentionCount$ = this.announcementNotifications.attentionCount$;

  newNoticesCount: number | null = null;
  newReservationCount: number | null = null;
  newOrdersCount: number | null = null;
  unconfirmedCount$ = this.billingService.getManagerUnconfirmedCount(false, false);
  projectId$ = this.store.select(selectCurrentProjectId);
  hasUnsignedOnboardingForms: boolean = false;
  unsignedForms: OnboardingFormSummary[] = [];

  constructor(public auth: OAuthService,
    private router: Router,
    private projectService: ProjectService,
    private reservationService: EquipmentReservationService,
    private arrivalService: ArrivalService,
    private warehouseService: WarehouseDataService,
    private store: Store,
    private billingService: BillingService,
    private onboardingService: OnboardingService,
    private confirmationService: ConfirmationService,
    private analytics: AnalyticsService,
    private logger: LoggerService,
    private cd: ChangeDetectorRef,
    private announcementNotifications: AnnouncementNotificationService
  ) { super(); }

  ngOnInit() {
    // Clear loading skeleton on first auth check completion (ReplaySubject fires immediately if already logged in)
    this.auth.loginChanged.pipe(take(1)).subscribe(() => {
      this.isLoading = false;
      this.cd.markForCheck();
    });

    // Track page view
    this.analytics.trackPageView('home', 'Home Dashboard', {
      hasArrivalsPermission: false,
      hasReservationsPermission: false,
      hasWarehousePermission: false
    });

    combineLatest([
      this.auth.loginChanged,
      from(this.auth.hasPermission(this.permissions.CanManageArrivals)),
      this.store.select(selectCurrentProject)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([loggedIn, hasPermission, project]) => {
        if (loggedIn && project) {
          this.currentProjectId = project.id;
          this.cd.markForCheck();
          if (hasPermission) {
            this.updateNewNoticesCount();
          }
        }
      });

    combineLatest([
      this.arrivalService.arrivalsChanged$,
      from(this.auth.hasPermission(this.permissions.CanManageArrivals))
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([_change, canManage]) => {
        if (canManage) {
          this.updateNewNoticesCount();
        }
      });

    this.checkUnsignedOnboardingForms();

    this.store.select(selectCurrentProject)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(project => {
        if (project) {
          this.address = {
            streetAddress: project.streetAddress,
            postalNumber: project.postalNumber,
            postalArea: project.postalArea,
            latitude: 0,
            longitude: 0
          };
          this.arrivalCalendarEnabled = project.settings?.arrivalCalendarEnabled ?? false;
          this.liftCalendarEnabled = project.settings?.liftCalendarEnabled ?? false;
          this.cd.markForCheck();
        }
      });
  }

  checkUnsignedOnboardingForms(): void {
    this.auth.getUserId().then(() => {
      this.auth.hasRole('Worker').then(hasRole => {
        if (hasRole) {
          // Only my own forms still waiting for my signature, filtered on the server and without personal data.
          this.onboardingService.getFormSummaries(0, true)
            .pipe(
              catchError(err => { this.logger.errorWithContext('HomeComponent', 'Failed to load onboarding forms', err); return of([]); }),
              takeUntil(this.destroy$)
            )
            .subscribe(forms => {
              const unsignedOnboardingForms = forms;
              this.unsignedForms = unsignedOnboardingForms;
              this.hasUnsignedOnboardingForms = unsignedOnboardingForms.length > 0;
              this.cd.markForCheck();
            });
        } else {
          this.hasUnsignedOnboardingForms = false;
          this.cd.markForCheck();
        }
      });
    });
  }

  navigateToOnboarding(form: OnboardingFormSummary) {
    this.analytics.trackAction('navigate_unsigned_onboarding', 'home', 'home-dashboard', 'Unsigned Form', {
      formId: form.formId,
      workerId: form.workerId
    });
    // By id, so a reload keeps the form; Home only lists the user's own unsigned forms, which they sign as worker.
    this.router.navigate(['onboarding/edit', form.formId], {
      queryParams: { mode: 'worker', download: false }
    });
  }

  navigateToUnsignedForms() {
    if (this.unsignedForms.length === 1) {
      this.navigateToOnboarding(this.unsignedForms[0]);
    } else {
      this.analytics.trackAction('navigate_unsigned_forms_list', 'home', 'home-dashboard', 'Multiple Unsigned Forms');
      this.router.navigate(['/onboarding']);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  updateNewNoticesCount() {
    if (this.currentProjectId) {
      const arrivalCount$ = this.arrivalService.getWaitingNoticesCount(this.currentProjectId).pipe(
        catchError(() => of(null)),
        tap((arrivalData) => this.handleNoticesCount(arrivalData, 'arrival'))
      );

      const reservationCount$ = this.reservationService.getWaitingNoticesCount(this.currentProjectId).pipe(
        catchError(() => of(null)),
        tap((reservationData) => this.handleNoticesCount(reservationData, 'reservation'))
      );

      const ordersCount$ = this.warehouseService.getWaitingOrdersCount().pipe(
        catchError(() => of(null)),
        tap((ordersData) => this.handleNoticesCount(ordersData, 'orders'))
      );

      merge(arrivalCount$, reservationCount$, ordersCount$)
        .pipe(takeUntil(this.destroy$))
        .subscribe();
    }
  }

  private handleNoticesCount(data: number | null, type: string) {
    const count = data !== null && data > 0 ? data : null;
    switch (type) {
      case 'arrival':
        this.newNoticesCount = count;
        break;
      case 'reservation':
        this.newReservationCount = count;
        break;
      case 'orders':
        this.newOrdersCount = count;
        break;
    }
    this.cd.markForCheck();
  }

  getBadgeCount() {
    const noticesCount = this.newNoticesCount !== null && this.newNoticesCount !== undefined ? this.newNoticesCount : 0;
    const reservationCount = this.newReservationCount !== null && this.newReservationCount !== undefined ? this.newReservationCount : 0;
    const total = noticesCount + reservationCount;
    return total > 0 ? total : null;
  }

  handleArrivalClick() {
    this.analytics.trackAction('click_arrival_card', 'home', 'home-dashboard', 'New Arrival');
    this.router.navigate([
      'projects',
      this.currentProjectId,
      'arrivals',
      'add',
    ]);
  }

  handleEquipmentClick() {
    this.analytics.trackAction('click_equipment_card', 'home', 'home-dashboard', 'Lift Reservation');
    this.router.navigate([
      'projects',
      this.currentProjectId,
      'reservations',
      'add',
    ]);
  }

  handleCalClicked() {
    this.analytics.trackAction('click_calendar_card', 'home', 'home-dashboard', 'Calendar');
    this.router.navigate([
      'projects',
      this.currentProjectId,
      'calendar',
      'month',
    ]);
  }

  handleBillingClick() {
    this.analytics.trackAction('click_billing_card', 'home', 'home-dashboard', 'Billing');
    this.auth.hasPermission(this.permissions.CanCreateSummary).then(canCreateSummary => {
      if (canCreateSummary) {
        this.analytics.trackAction('navigate_billing_create', 'home', 'home-dashboard', 'Create Summary');
        this.router.navigate(['/billing', 'create']);
      } else {
        this.auth.hasPermission([this.permissions.CanConfirmSummary, this.permissions.CanConfirmAllSummaries]).then(canConfirm => {
          if (canConfirm) {
            this.analytics.trackAction('navigate_billing_confirm', 'home', 'home-dashboard', 'Confirm Summary');
            this.router.navigate(['/billing', 'confirm']);
          } else {
            this.analytics.trackAction('billing_access_denied', 'home', 'home-dashboard', 'No Permission');
          }
        });
      }
    });
  }

  handleCleaningPlanClick() {
    this.analytics.trackAction('click_cleaning_plan_card', 'home', 'home-dashboard', 'Cleaning Plan');
    this.router.navigate(['projects', 'cleaning-plan']);
  }


}
